import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone,
  Mail,
  User,
  MapPin,
  Calendar,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Send,
} from 'lucide-react';
import axios from 'axios';
import { API } from '../config';
import { useAuth } from '../context/AuthContext';
import { auth, googleProvider } from '../lib/firebase';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';

const EMAIL_SIGN_IN_KEY = 'vastuspace_emailForSignIn';

const Login = () => {
  const navigate = useNavigate();
  const { user, setUser, loginWithFirebaseToken } = useAuth();

  const [step, setStep] = useState('choose'); // choose | phone | email | otp | email-sent | register
  const [loginType, setLoginType] = useState(null); // 'phone' | 'email'
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Registration fields (for new Firebase users)
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [age, setAge] = useState('');

  const confirmationResultRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  // Handle Firebase Redirect result (for Google Sign-in in the same window)
  useEffect(() => {
    if (!auth) return;

    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          setLoading(true);
          setError('');
          const idToken = await result.user.getIdToken();
          const data = await loginWithFirebaseToken(idToken);

          if (data?.is_new_user) {
            const email = result.user.email;
            if (email) {
              setIdentifier(email);
              setLoginType('email');
            }
            setStep('register');
          } else {
            navigate('/dashboard');
          }
        }
      } catch (err) {
        console.error('Redirect sign-in error:', err);
        setError(err.message || 'Failed to sign in with Google');
      } finally {
        setLoading(false);
      }
    };

    checkRedirectResult();
  }, [auth, loginWithFirebaseToken, navigate]);

  // Handle Firebase Email Link return
  useEffect(() => {
    if (!auth) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    const completeEmailSignIn = async () => {
      let email = window.localStorage.getItem(EMAIL_SIGN_IN_KEY);
      if (!email) {
        email = window.prompt('Please re-enter your email to complete sign-in:');
      }
      if (!email) return;

      setLoading(true);
      setError('');
      try {
        const result = await signInWithEmailLink(auth, email, window.location.href);
        window.localStorage.removeItem(EMAIL_SIGN_IN_KEY);
        window.history.replaceState({}, document.title, window.location.pathname);

        const idToken = await result.user.getIdToken();
        const data = await loginWithFirebaseToken(idToken);

        if (data?.is_new_user) {
          setIdentifier(email);
          setLoginType('email');
          setStep('register');
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Email link sign-in error:', err);
        setError('Failed to complete email sign-in. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    completeEmailSignIn();
  }, [auth, loginWithFirebaseToken, navigate]);

  // ─── reCAPTCHA helper ───────────────────────────────────────────────────────
  const setupRecaptcha = () => {
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        'recaptcha-container',
        { size: 'invisible', callback: () => {} }
      );
    }
    return recaptchaVerifierRef.current;
  };

  const clearRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  };

  // ─── Google Sign-In ──────────────────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    if (!auth) {
      setError('Google sign-in is not configured. Please contact support.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signInWithRedirect(auth, googleProvider);
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(err.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  // ─── Send OTP / Magic Link ───────────────────────────────────────────────────
  const handleSendOtp = async () => {
    setError('');

    if (!identifier) {
      setError(loginType === 'phone' ? 'Please enter your phone number' : 'Please enter your email');
      return;
    }
    if (loginType === 'phone' && !/^\d{10}$/.test(identifier.replace(/\D/g, ''))) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    if (loginType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      if (!auth) {
        // No Firebase configured, fall back to local backend OTP logic
        const isPhone = loginType === 'phone';
        const payload = isPhone 
          ? { phone: identifier.replace(/\D/g, '') } 
          : { email: identifier };
          
        await axios.post(`${API}/auth/send-otp`, payload);
        alert(`Developer OTP bypass is active! Use code 123456 (or check server console log for generated code)`);
        setStep('otp');
        return;
      }

      if (loginType === 'phone') {
        // Firebase Phone Auth — Firebase sends the SMS
        const verifier = setupRecaptcha();
        const phoneE164 = `+91${identifier.replace(/\D/g, '')}`;
        const confirmationResult = await signInWithPhoneNumber(auth, phoneE164, verifier);
        confirmationResultRef.current = confirmationResult;
        setStep('otp');
      } else {
        // Firebase Email Link — Firebase sends the magic-link email
        const actionCodeSettings = {
          url: window.location.href,
          handleCodeInApp: true,
        };
        await sendSignInLinkToEmail(auth, identifier, actionCodeSettings);
        window.localStorage.setItem(EMAIL_SIGN_IN_KEY, identifier);
        setStep('email-sent');
      }
    } catch (err) {
      console.error('Send verification error:', err);
      clearRecaptcha();
      setError(err.response?.data?.detail || err.message || 'Failed to send verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Verify Phone OTP ────────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (!auth) {
        // No Firebase configured, verify against backend
        const isPhone = loginType === 'phone';
        const payload = {
          otp,
          ...(isPhone 
            ? { phone: identifier.replace(/\D/g, '') } 
            : { email: identifier })
        };
        const response = await axios.post(`${API}/auth/verify-otp`, payload, {
          withCredentials: true
        });

        if (response.data.is_new_user) {
          setStep('register');
        } else {
          setUser(response.data);
          navigate('/dashboard');
        }
        return;
      }

      if (!confirmationResultRef.current) {
        setError('Session expired. Please request a new OTP.');
        return;
      }
      const result = await confirmationResultRef.current.confirm(otp);
      const idToken = await result.user.getIdToken();
      const data = await loginWithFirebaseToken(idToken);

      if (data?.is_new_user) {
        setStep('register');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Register new user (name / city / age) ───────────────────────────────────
  const handleRegister = async () => {
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!city.trim()) { setError('Please enter your city'); return; }
    if (!age || parseInt(age) < 13 || parseInt(age) > 120) {
      setError('Please enter a valid age (13–120)');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        city: city.trim(),
        age: parseInt(age),
        ...(loginType === 'phone'
          ? { phone: identifier.replace(/\D/g, '') }
          : { email: identifier }),
      };
      const response = await axios.post(`${API}/auth/register`, payload, {
        withCredentials: true,
      });
      if (typeof setUser === 'function') setUser(response.data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── Render helpers ──────────────────────────────────────────────────────────
  const renderChooseMethod = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-[#232323] text-center mb-6">
        Welcome to Vastuspace.AI
      </h2>
      <p className="text-[#353535] text-center mb-8">Choose how you'd like to continue</p>

      {/* Google */}
      <button
        onClick={handleGoogleLogin}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-300 rounded-xl hover:border-[#232323] hover:shadow-md transition-all disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        )}
        <span className="font-semibold text-[#232323]">Continue with Google</span>
      </button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-[#353535]">or</span>
        </div>
      </div>

      {/* Phone */}
      <button
        onClick={() => { setLoginType('phone'); setStep('phone'); }}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all"
      >
        <Phone className="w-5 h-5" />
        <span className="font-semibold">Continue with Phone</span>
      </button>

      {/* Email */}
      <button
        onClick={() => { setLoginType('email'); setStep('email'); }}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-[#232323] text-[#232323] rounded-xl hover:bg-gray-50 transition-all"
      >
        <Mail className="w-5 h-5" />
        <span className="font-semibold">Continue with Email</span>
      </button>
    </div>
  );

  const renderPhoneInput = () => (
    <div className="space-y-4">
      <button onClick={() => { setStep('choose'); setError(''); }} className="flex items-center gap-2 text-[#353535] hover:text-[#232323] mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-2xl font-bold text-[#232323] mb-2">Enter your phone number</h2>
      <p className="text-[#353535] mb-6">Firebase will send you a one-time SMS code</p>

      <div className="flex gap-2">
        <div className="flex items-center px-3 py-4 border-2 border-gray-300 rounded-xl bg-gray-50 text-[#232323] font-semibold select-none">
          🇮🇳 +91
        </div>
        <div className="relative flex-1">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#353535]" />
          <input
            type="tel"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="10-digit mobile number"
            className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#232323] focus:outline-none text-lg"
            maxLength={10}
          />
        </div>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Invisible reCAPTCHA anchor */}
      <div id="recaptcha-container" />

      <button
        onClick={handleSendOtp}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send OTP via SMS'}
      </button>
    </div>
  );

  const renderEmailInput = () => (
    <div className="space-y-4">
      <button onClick={() => { setStep('choose'); setError(''); }} className="flex items-center gap-2 text-[#353535] hover:text-[#232323] mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-2xl font-bold text-[#232323] mb-2">Enter your email</h2>
      <p className="text-[#353535] mb-6">Firebase will send you a sign-in link</p>

      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#353535]" />
        <input
          type="email"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          placeholder="your@email.com"
          className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#232323] focus:outline-none text-lg"
        />
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        onClick={handleSendOtp}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Sign-in Link'}
      </button>
    </div>
  );

  const renderEmailSent = () => (
    <div className="space-y-4 text-center">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <Send className="w-8 h-8 text-green-600" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-[#232323]">Check your email</h2>
      <p className="text-[#353535]">
        We sent a sign-in link to <strong>{identifier}</strong>.<br />
        Click the link in the email to continue.
      </p>
      <p className="text-sm text-[#353535] mt-4">
        Didn't get it?{' '}
        <button
          onClick={() => { setStep('email'); setError(''); }}
          className="text-[#232323] font-semibold hover:underline"
        >
          Try again
        </button>
      </p>
    </div>
  );

  const renderOtpInput = () => (
    <div className="space-y-4">
      <button
        onClick={() => { setStep('phone'); setError(''); setOtp(''); clearRecaptcha(); }}
        className="flex items-center gap-2 text-[#353535] hover:text-[#232323] mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center gap-2 mb-4">
        <CheckCircle className="w-5 h-5 text-green-500" />
        <span className="text-green-600">SMS sent successfully!</span>
      </div>

      <h2 className="text-2xl font-bold text-[#232323] mb-2">Enter OTP</h2>
      <p className="text-[#353535] mb-6">
        Enter the 6-digit code sent to +91 {identifier}
      </p>

      <input
        type="text"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="— — — — — —"
        className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#232323] focus:outline-none text-center text-2xl tracking-widest"
        maxLength={6}
      />

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        onClick={handleVerifyOtp}
        disabled={loading || otp.length !== 6}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify OTP'}
      </button>

      <button
        onClick={() => { setOtp(''); setError(''); clearRecaptcha(); handleSendOtp(); }}
        className="w-full text-[#353535] hover:text-[#232323] text-sm"
      >
        Didn't receive? Resend OTP
      </button>
    </div>
  );

  const renderRegistration = () => (
    <div className="space-y-4">
      <button onClick={() => { setStep('choose'); setError(''); }} className="flex items-center gap-2 text-[#353535] hover:text-[#232323] mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-2xl font-bold text-[#232323] mb-2">Complete your profile</h2>
      <p className="text-[#353535] mb-6">Tell us a bit about yourself for personalised Vastu recommendations</p>

      <div className="relative">
        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#353535]" />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name"
          className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#232323] focus:outline-none"
        />
      </div>

      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#353535]" />
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Your city"
          className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#232323] focus:outline-none"
        />
      </div>

      <div className="relative">
        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#353535]" />
        <input
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          placeholder="Your age"
          min="13"
          max="120"
          className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#232323] focus:outline-none"
        />
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      <button
        onClick={handleRegister}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#232323] text-white rounded-xl hover:bg-[#353535] transition-all disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
      </button>

      <p className="text-xs text-[#353535] text-center">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo.jpg"
            alt="Vastuspace.AI Logo"
            className="w-16 h-16 object-contain rounded-full mx-auto mb-4 border border-[#826239]/30 shadow-md animate-float"
          />
          <h1 className="text-xl font-bold text-[#352520] tracking-tight">Vastuspace.AI</h1>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#EBE6DD]/60 rounded-2xl shadow-xl p-8">
          {step === 'choose'     && renderChooseMethod()}
          {step === 'phone'      && renderPhoneInput()}
          {step === 'email'      && renderEmailInput()}
          {step === 'email-sent' && renderEmailSent()}
          {step === 'otp'        && renderOtpInput()}
          {step === 'register'   && renderRegistration()}
        </div>

        <p className="text-center text-sm text-[#826239] mt-6">
          Need help?{' '}
          <a href="/contact" className="text-[#D97706] font-semibold hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
